import React, { useMemo, useState } from 'react';
import {
  DndContext,
  DragOverlay,
  MouseSensor,
  TouchSensor,
  closestCorners,
  useDroppable,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Box, CircularProgress, IconButton, Tooltip } from '@mui/material';
import { MoreVert as MoreVertIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { DOCUMENT } from '../constants/documentTheme';
import {
  buildDragReorderUpdates,
  getDayDateKey,
  getDayDroppableId,
  isDraggableItineraryItem,
  resolveDragTarget,
} from '../utils/itineraryDragUtils';
import {
  buildRoadTripDragUpdates,
  findRoadTripCardById,
  isDraggableRoadTripCard,
} from '../utils/roadTripDragUtils';

// Mouse + Touch (not Pointer) so scrollable itineraries still scroll on mobile,
// while a short press-and-hold on the handle starts a drag.
export const ITINERARY_MOUSE_SENSOR_OPTIONS = {
  activationConstraint: { distance: 8 },
};

export const ITINERARY_TOUCH_SENSOR_OPTIONS = {
  activationConstraint: { delay: 250, tolerance: 8 },
};

const allowAllItems = () => true;

const ItineraryDragDropContext = React.createContext({
  disabled: true,
  isSaving: false,
  activeItemId: null,
  canDragItem: allowAllItems,
});

const SortableItineraryItem = ({
  item,
  dragEnabled,
  children,
}) => {
  const { t } = useTranslation();
  const { disabled, isSaving } = React.useContext(ItineraryDragDropContext);
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: item.id,
    disabled: disabled || isSaving || !dragEnabled,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.45 : 1,
    position: 'relative',
  };

  const showHandle = dragEnabled && !disabled && !isSaving;

  return (
    <Box
      ref={setNodeRef}
      style={style}
      data-testid="itinerary-drag-row"
      sx={showHandle ? { display: 'flex', alignItems: 'flex-start', gap: 0.75 } : undefined}
    >
      {showHandle && (
        <Tooltip
          title={t('itinerary.dragToReorder')}
          arrow
          // Long-press tooltips fight TouchSensor activation on mobile.
          disableTouchListener
        >
          <IconButton
            size="small"
            ref={setActivatorNodeRef}
            aria-label={t('itinerary.dragToReorder')}
            data-testid="itinerary-drag-handle"
            {...attributes}
            {...listeners}
            onClick={(event) => event.stopPropagation()}
            // Inline touch-action is required before touchstart; CSS-in-JS can be too late.
            style={{ touchAction: 'none', WebkitUserSelect: 'none', userSelect: 'none' }}
            sx={{
              position: 'relative',
              flexShrink: 0,
              mt: 1.75,
              zIndex: 2,
              width: 18,
              height: 32,
              p: 0,
              borderRadius: '10px',
              cursor: 'grab',
              color: 'text.secondary',
              bgcolor: DOCUMENT.surface,
              border: `1px solid ${DOCUMENT.ruleHard}`,
              boxShadow: 'none',
              touchAction: 'none',
              WebkitUserSelect: 'none',
              userSelect: 'none',
              '&:active': { cursor: 'grabbing' },
            }}
          >
            <MoreVertIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
      )}
      <Box sx={{ flex: 1, minWidth: 0 }}>{children}</Box>
    </Box>
  );
};

export const ItineraryDayDragArea = ({
  day,
  renderItem,
  renderRoadTripCard,
  renderAddActivitySection,
  renderEmptyDaySection,
}) => {
  const {
    disabled, isSaving, activeItemId, canDragItem,
  } = React.useContext(ItineraryDragDropContext);
  const dayKey = getDayDateKey(day.date);
  const hasItems = day.items.length > 0;
  const { setNodeRef, isOver } = useDroppable({ id: getDayDroppableId(dayKey) });

  const sortableItems = day.items
    .filter((item) => (isDraggableItineraryItem(item) || isDraggableRoadTripCard(item)) && canDragItem(item))
    .map((item) => item.id);
  // One stop can still be dragged onto another day.
  const canReorder = sortableItems.length > 0;

  return (
    <Box
      ref={setNodeRef}
      sx={{
        position: 'relative',
        minHeight: activeItemId ? 56 : undefined,
        borderRadius: 2,
        outline: isOver ? `2px dashed ${DOCUMENT.accent}` : 'none',
        outlineOffset: 4,
        transition: 'outline-color 0.2s ease',
      }}
    >
      {hasItems ? (
        <SortableContext items={sortableItems} strategy={verticalListSortingStrategy}>
          <Box sx={{ position: 'relative' }}>
            {isSaving && (
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  zIndex: 30,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: 'rgba(255,255,255,0.55)',
                  borderRadius: 2,
                }}
              >
                <CircularProgress size={28} />
              </Box>
            )}

            {day.items.map((item, index) => {
              if (item._isRoadTripCard) {
                const dragEnabled = !disabled && canReorder && isDraggableRoadTripCard(item) && canDragItem(item);
                return (
                  <SortableItineraryItem
                    key={`rt-${item._roadTripGroupId}-${item._roadTripData?.dayIndex ?? item.id}`}
                    item={item}
                    dragEnabled={dragEnabled}
                  >
                    {renderRoadTripCard(item)}
                  </SortableItineraryItem>
                );
              }

              const dragEnabled = !disabled && canReorder && isDraggableItineraryItem(item) && canDragItem(item);

              return (
                <SortableItineraryItem
                  key={item.id}
                  item={item}
                  dragEnabled={dragEnabled}
                >
                  {renderItem(item, index === day.items.length - 1, day.date)}
                </SortableItineraryItem>
              );
            })}

            {renderAddActivitySection(day)}
          </Box>
        </SortableContext>
      ) : (
        <>
          {renderEmptyDaySection(day)}
          {activeItemId && (
            <Box sx={{ mt: 1, minHeight: 48 }} />
          )}
        </>
      )}
    </Box>
  );
};

const ItineraryDragDropProvider = ({
  days,
  disabled = false,
  isSaving = false,
  renderItem,
  renderRoadTripCard,
  onReorder,
  tripStartDate = null,
  canDragItem = allowAllItems,
  children,
}) => {
  const [activeItemId, setActiveItemId] = useState(null);

  const sensors = useSensors(
    useSensor(MouseSensor, ITINERARY_MOUSE_SENSOR_OPTIONS),
    useSensor(TouchSensor, ITINERARY_TOUCH_SENSOR_OPTIONS)
  );

  const activeItem = useMemo(() => {
    if (!activeItemId) {
      return null;
    }

    for (const day of days) {
      const match = day.items.find((item) => item.id === activeItemId);
      if (match) {
        return match;
      }
    }
    return null;
  }, [activeItemId, days]);

  const handleDragStart = (event) => {
    setActiveItemId(event.active.id);
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    setActiveItemId(null);

    if (!over || disabled || isSaving) {
      return;
    }

    const activeId = active.id;
    const dragTarget = resolveDragTarget({
      days,
      activeItemId: activeId,
      overId: over.id,
    });

    if (!dragTarget) {
      return;
    }

    const roadTripMatch = findRoadTripCardById(days, activeId);
    if (roadTripMatch?.card) {
      const sourceDayKey = dragTarget.sourceDayKey || roadTripMatch.sourceDayKey;
      if (!sourceDayKey || sourceDayKey === dragTarget.targetDayKey) {
        return;
      }

      const updates = buildRoadTripDragUpdates({
        roadTripCard: roadTripMatch.card,
        sourceDayKey,
        targetDayKey: dragTarget.targetDayKey,
        tripStartDate,
      });

      if (!updates.length) {
        return;
      }

      await onReorder(updates);
      return;
    }

    const sourceDay = days.find((day) => day.items.some((item) => item.id === activeId));
    const sourceDayKey = dragTarget.sourceDayKey || (sourceDay ? getDayDateKey(sourceDay.date) : null);

    if (!sourceDayKey) {
      return;
    }

    const sourceIndex = sourceDay?.items.findIndex((item) => item.id === activeId) ?? -1;
    if (sourceDayKey === dragTarget.targetDayKey && sourceIndex === dragTarget.targetIndex) {
      return;
    }

    const itemsById = new Map(days.flatMap((day) => day.items.map((item) => [item.id, item])));
    // Items the viewer can't move (e.g. the group's shared plan) keep their times.
    const updates = buildDragReorderUpdates({
      days,
      activeItemId: activeId,
      sourceDayKey,
      targetDayKey: dragTarget.targetDayKey,
      targetIndex: dragTarget.targetIndex,
    }).filter((update) => {
      const item = itemsById.get(update.id);
      return !item || canDragItem(item);
    });

    if (!updates.length) {
      return;
    }

    await onReorder(updates);
  };

  const handleDragCancel = () => {
    setActiveItemId(null);
  };

  const contextValue = useMemo(
    () => ({
      disabled, isSaving, activeItemId, canDragItem,
    }),
    [disabled, isSaving, activeItemId, canDragItem]
  );

  return (
    <ItineraryDragDropContext.Provider value={contextValue}>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCorners}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
      >
        {children}
        <DragOverlay dropAnimation={null}>
          {activeItem ? (
            <Box sx={{ opacity: 0.9, pointerEvents: 'none', transform: 'rotate(1deg)' }}>
              {activeItem._isRoadTripCard && renderRoadTripCard
                ? renderRoadTripCard(activeItem)
                : renderItem(activeItem, false, null)}
            </Box>
          ) : null}
        </DragOverlay>
      </DndContext>
    </ItineraryDragDropContext.Provider>
  );
};

export default ItineraryDragDropProvider;
